export const DELETION_STEPS = [
  {
    id: 1,
    slug: "open-account-management",
    navLabel: "Open Account Management",
    title: "Open Account Management",
    image: "/assets/pages/account-change-request/step-1-profile-account-management.png",
    imageAlt: "RealtorsIn Profile screen showing Account Management under Settings",
    caption: "Profile → Settings → Account Management",
    sections: [
      {
        heading: "Sign in to the app",
        body: "Open the RealtorsIn app on your phone and sign in with the account you want to delete.",
      },
      {
        heading: "Open your Profile",
        body: "Tap the Profile tab in the bottom navigation bar (far right icon).",
      },
      {
        heading: "Go to Account Management",
        body: "Scroll to the Settings section and tap Account Management. This screen is where you can change your password or start account deletion.",
      },
    ],
    note: "If you cannot find Account Management, update the app from the App Store or Google Play and try again.",
  },
  {
    id: 2,
    slug: "choose-deletion-method",
    navLabel: "Choose Deletion Method",
    title: "Choose How to Delete Your Account",
    image: "/assets/pages/account-change-request/step-2-account-management.png",
    imageAlt: "Account Management screen with Delete Immediately and Schedule for Later options",
    caption: "Account Management — deletion options",
    sections: [
      {
        heading: "Read the deletion warning",
        body: "On the Account Management screen, review the warning. Deleting your account permanently removes your posts, comments, likes, connections, messages, profile, and all personal data. This cannot be undone.",
      },
      {
        heading: "Pick one of two options",
        body: null,
        options: [
          {
            title: "Delete Immediately",
            description: "Permanently deletes your account and data right away.",
          },
          {
            title: "Schedule for Later (7 days)",
            description:
              "Marks your account for deletion after a 7-day grace period. Log in again before the period ends to cancel.",
          },
        ],
      },
    ],
    note: "Need help before deleting? Use the support banner on this screen to contact our team.",
  },
  {
    id: 3,
    slug: "review-deletion-preview",
    navLabel: "Review Deletion Preview",
    title: "Review the Account Deletion Preview",
    image: "/assets/pages/account-change-request/step-3-deletion-preview.png",
    imageAlt: "Account Deletion Preview showing data that will be permanently removed",
    caption: "Account Deletion Preview — summary of removed data",
    sections: [
      {
        heading: "What the preview shows",
        body: "Before anything is removed, RealtorsIn displays an Account Deletion Preview listing exactly what will be deleted.",
      },
      {
        heading: "Review your data summary",
        body: "Your name, email, phone, and username appear at the top, followed by a total count and a breakdown: user-generated content (posts, comments, likes, reposts), social connections, and chat messages.",
      },
      {
        heading: "Continue or go back",
        body: "This action cannot be undone. When you have reviewed the summary, tap Proceed to Delete to continue or Cancel to return to Account Management.",
      },
    ],
  },
  {
    id: 4,
    slug: "confirm-deletion",
    navLabel: "Confirm Deletion",
    title: "Confirm Deletion",
    image: "/assets/pages/account-change-request/step-4-schedule-confirmation.png",
    imageAlt: "Schedule Account Deletion confirmation modal with 7-day grace period",
    caption: "Final confirmation — scheduled or immediate deletion",
    sections: [
      {
        heading: "If you chose Schedule for Later",
        body: "A confirmation modal explains that your account will be marked inactive and scheduled for permanent deletion in 7 days. You can cancel by signing in again before the grace period ends. Tap Schedule Deletion to confirm, or Cancel to go back.",
      },
      {
        heading: "If you chose Delete Immediately",
        body: "You will see a similar final confirmation screen before your account and data are permanently removed from our servers.",
      },
      {
        heading: "After you confirm",
        body: "You will be logged out once the request is submitted. Scheduled deletions can still be reversed within 7 days by signing back in.",
      },
    ],
  },
] as const;

export type DeletionStep = (typeof DELETION_STEPS)[number];

export const GUIDE_NAV = [
  { id: "guide-overview", label: "Overview" },
  ...DELETION_STEPS.map((s) => ({
    id: `guide-${s.slug}`,
    label: `${s.id}. ${s.navLabel}`,
  })),
];
